import React, { useEffect } from 'react';
import RsTooltip from '@Pages/RetroSynthesis/components/RsTooltip';
import { StructureOptionType } from '@Pages/RetroSynthesis/StructureOption/constants';
import StructureToolbarOptionsContent from '@Components/Results/structure/StructureToolbarOptionsContent';
import { loadShoppingCartImage } from '@Components/Results/structure/availability/ShoppingCartHelper';
import AvailabilityProviders from '@Components/Results/structure/availability/AvailabilityProviders';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import StructureOptionsItem from '@Components/Results/structure/constants';
import {
  onCopyToQueryClicked,
  onFindSimilarClicked,
  onOpenInDatabaseClicked,
  onShoppingCartOptionSelected,
  onSynthesisPlannerClicked,
  onViewRelatedMarkushClicked,
} from '@Pages/RetroSynthesis/ProjectRoute/analytics';
import SubstanceDetailsModal from '@Components/Results/structure/substance-details/SubstanceDetailsModal';
import { StructureToolbarContextMenuOptions } from '@Pages/RetroSynthesis/components/RsStructure/constants';
import { copySmiles } from '@Components/Results/structure/structure-utils';
import { StructureOptionHelper } from './StructureOptionHelper';

import './StructureOption.scss';

const metadata = {
  fromSynthesisPlanner: true,
};

const optionTitles = {
  [StructureOptionType.OPTIONS]: 'Options',
  [StructureOptionType.SHOPPING_CARD]: 'Substance Availability',
  [StructureOptionType.DETAILS]: 'Substance details',
};

function StructureOption({
  optionType,
  targetRect,
  data,
  synthesisMenuOptions,
  trackingContext,
  onClosed = () => null,
  openStructureIn3d,
}) {
  useEffect(
    () => () => {
      onClosed();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!optionType || !targetRect || !data) {
    return null;
  }

  const structure = StructureOptionHelper.extractData(data);

  if (!structure) {
    return null;
  }

  const synthesisMenuOptionsForRouteType = synthesisMenuOptions;

  if (optionType === StructureOptionType.ZOOM) {
    openStructureIn3d(uuid(), structure.molImage.structure);
    onClosed();
    return null;
  }

  if (optionType === StructureOptionType.CUSTOMER_ICON) {
    copySmiles(structure.molImage.structure);
    return null;
  }

  return (
    <RsTooltip targetRect={targetRect} onClosed={onClosed} renderCloseBtn title={optionTitles[optionType]}>
      {optionType === StructureOptionType.OPTIONS && (
        <StructureToolbarOptionsContent
          allowedOptionsItems={StructureToolbarContextMenuOptions.allowed}
          className="structure-option__content"
          metadata={metadata}
          structure={structure}
          synthesisOptions={synthesisMenuOptionsForRouteType}
          onClosed={() => onClosed()}
          onItemClick={(item) => {
            // eslint-disable-next-line default-case
            switch (item) {
              case StructureOptionsItem.FIND_SIMILAR:
                onFindSimilarClicked(trackingContext);
                break;
              case StructureOptionsItem.VIEW_RELATED_MARKUSH:
                onViewRelatedMarkushClicked(trackingContext);
                break;
              case StructureOptionsItem.COPY_STRUCTURE_TO_QUERY:
                onCopyToQueryClicked(trackingContext);
                break;
            }
          }}
          onOpenInDatabaseOptionClick={(option) => onOpenInDatabaseClicked({ ...trackingContext, option })}
          onAutoSynthesizeOptionClick={() => onSynthesisPlannerClicked(trackingContext)}
        />
      )}
      {optionType === StructureOptionType.SHOPPING_CARD && (
        <AvailabilityProviders
          className="structure-option__content"
          closeMenu={() => onClosed()}
          providers={structure && structure.availability && structure.availability.providers}
          loadImage={loadShoppingCartImage}
          structure={structure}
          details={structure.details}
          providersArePreload
          onProviderClick={(option) => onShoppingCartOptionSelected({ ...trackingContext, option })}
        />
      )}
      {optionType === StructureOptionType.DETAILS && (
        <div className="structure-option__details">
          <SubstanceDetailsModal structure={structure} reactionId={structure.rxn} loadDetailsOnMount closeMenu={() => onClosed()} isOpen />
        </div>
      )}
    </RsTooltip>
  );
}

StructureOption.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  synthesisOptions: PropTypes.array,
  trackingContext: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  context: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  metadata: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  onToggleMenu: PropTypes.func,
  onClosed: PropTypes.func,
  optionType: PropTypes.oneOf(StructureOptionType.allTypes),
};

export default StructureOption;
